import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useT } from '../i18n/I18nContext';
import type { Translations } from '../i18n/translations/ru';
import { useTrackSection } from '../hooks/useTrackSection';

// ── Shared card style (matches ProblemSection, with refined polish) ──────────
const cardBase: React.CSSProperties = {
  background: 'rgba(255,255,255,0.10)',
  backdropFilter: 'blur(24px)',
  WebkitBackdropFilter: 'blur(24px)',
  border: '1px solid rgba(255,255,255,0.20)',
  borderRadius: 22,
  padding: 'clamp(22px, 2.6vw, 30px)',
  boxShadow: '0 8px 32px rgba(40,20,80,0.12), 0 1px 0 rgba(255,255,255,0.10) inset',
  position: 'relative',
  overflow: 'hidden',
};

// Header line: small icon + uppercase label
function CardHeader({ label, icon, accent }: { label: string; icon: React.ReactNode; accent: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18 }}>
      <div style={{
        width: 32, height: 32, borderRadius: 10,
        background: accent,
        border: '1px solid rgba(255,255,255,0.20)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0,
      }}>
        {icon}
      </div>
      <span style={{
        fontSize: 11, fontWeight: 700,
        letterSpacing: '0.10em', textTransform: 'uppercase',
        color: 'rgba(255,255,255,0.62)',
      }}>{label}</span>
    </div>
  );
}

// ── Card 1: Product swaps — the most concrete value the AI brings ────────────
// Anyone can read a generic "use ceramides" article. Nobody can tell you, from
// your *own* shelf, which jar to keep, which to bin, and what to add.
function ProductSwapsCard({ t }: { t: Translations }) {
  const swaps = t.preview.swaps.rows.map((r, i) => ({
    ...r,
    status: (i === 0 ? 'replace' : i === 1 ? 'ok' : 'missing') as 'ok' | 'replace' | 'missing',
  }));

  return (
    <div style={cardBase}>
      <CardHeader
        label={t.preview.swaps.title}
        accent="rgba(243,107,175,0.20)"
        icon={
          <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
            <rect x="2.5" y="3.5" width="11" height="10" rx="1.5" stroke="white" strokeWidth="1.4"/>
            <path d="M5.5 3.5V2.5a1 1 0 012 0v1M8.5 3.5V2.5a1 1 0 012 0v1" stroke="white" strokeWidth="1.2" strokeLinecap="round"/>
            <path d="M5 8h6M5 11h4" stroke="white" strokeWidth="1.3" strokeLinecap="round"/>
          </svg>
        }
      />

      <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
        {swaps.map((s, i) => (
          <div key={i} style={{
            padding: '12px 13px',
            background: 'rgba(255,255,255,0.05)',
            border: '1px solid rgba(255,255,255,0.10)',
            borderRadius: 12,
          }}>
            {/* Top row: from → to + status pill */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginBottom: 6 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                {s.status === 'replace' && s.from && (
                  <>
                    <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.42)', textDecoration: 'line-through', marginBottom: 4, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {s.from}
                    </p>
                    <p style={{ fontSize: 13.5, color: 'white', fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      → {s.to}
                    </p>
                  </>
                )}
                {s.status === 'ok' && (
                  <p style={{ fontSize: 13.5, color: 'white', fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {s.to}
                  </p>
                )}
                {s.status === 'missing' && (
                  <>
                    <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.42)', marginBottom: 4 }}>
                      {t.preview.swaps.missing}
                    </p>
                    <p style={{ fontSize: 13.5, color: 'white', fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      + {s.to}
                    </p>
                  </>
                )}
              </div>
              <StatusPill status={s.status} t={t} />
            </div>
            {/* Reason (small caption) */}
            {s.note && (
              <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.50)', lineHeight: 1.45 }}>
                {s.note}
              </p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Card 2: Personalized actives — *why* each one for *your* skin ────────────
function PersonalIngredientsCard({ t }: { t: Translations }) {
  const items = t.preview.ingredients.items;

  return (
    <div style={cardBase}>
      <CardHeader
        label={t.preview.ingredients.title}
        accent="rgba(183,240,223,0.22)"
        icon={
          <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
            <circle cx="8" cy="8" r="2.5" fill="white" opacity="0.95" />
            <circle cx="3.5" cy="4" r="1.6" fill="white" opacity="0.65" />
            <circle cx="12.5" cy="4" r="1.6" fill="white" opacity="0.65" />
            <circle cx="8" cy="13" r="1.6" fill="white" opacity="0.65" />
            <line x1="8" y1="5.5" x2="4.7" y2="5" stroke="white" strokeWidth="1" opacity="0.5" />
            <line x1="8" y1="5.5" x2="11.3" y2="5" stroke="white" strokeWidth="1" opacity="0.5" />
            <line x1="8" y1="10.5" x2="8" y2="11.4" stroke="white" strokeWidth="1" opacity="0.5" />
          </svg>
        }
      />

      <div style={{ display: 'flex', flexDirection: 'column', gap: 13 }}>
        {items.map((it, i) => (
          <div key={i} style={{ display: 'flex', gap: 11, alignItems: 'flex-start' }}>
            <div style={{
              width: 5, height: 5, borderRadius: '50%',
              background: 'rgba(255,255,255,0.40)', flexShrink: 0, marginTop: 8,
            }} />
            <div>
              <p style={{ fontSize: 14, fontWeight: 700, color: 'white', marginBottom: 3, letterSpacing: '-0.01em' }}>
                {it.name}
              </p>
              <p style={{ fontSize: 12.5, color: 'rgba(255,255,255,0.58)', lineHeight: 1.5 }}>
                {it.reason}
              </p>
            </div>
          </div>
        ))}
      </div>

      <div style={{
        marginTop: 14,
        padding: '9px 12px',
        background: 'rgba(255,255,255,0.05)',
        border: '1px solid rgba(255,255,255,0.10)',
        borderRadius: 10,
      }}>
        <p style={{ fontSize: 11.5, color: 'rgba(255,255,255,0.50)', lineHeight: 1.5 }}>
          {t.preview.ingredients.caption}
        </p>
      </div>
    </div>
  );
}

// ── Card 4: Timeline — what to expect when (concrete, motivating) ────────────
function TimelineCard({ t }: { t: Translations }) {
  const steps = t.preview.timeline.steps;

  return (
    <div style={cardBase}>
      <CardHeader
        label={t.preview.timeline.title}
        accent="rgba(139,124,255,0.22)"
        icon={
          <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
            <circle cx="8" cy="8" r="6" stroke="white" strokeWidth="1.4" />
            <path d="M8 5v3.5l2.5 1.5" stroke="white" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        }
      />

      <div style={{ position: 'relative', paddingLeft: 22 }}>
        {/* Vertical track */}
        <div style={{
          position: 'absolute', left: 7, top: 6, bottom: 6,
          width: 1, background: 'rgba(255,255,255,0.16)',
        }} />

        {steps.map((s, i) => (
          <div key={i} style={{ position: 'relative', marginBottom: i < steps.length - 1 ? 18 : 0 }}>
            {/* Node */}
            <div style={{
              position: 'absolute', left: -22, top: 4,
              width: 15, height: 15, borderRadius: '50%',
              background: 'rgba(255,255,255,0.07)',
              border: '1px solid rgba(255,255,255,0.30)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <div style={{ width: 5, height: 5, borderRadius: '50%', background: 'rgba(255,255,255,0.65)' }} />
            </div>
            <p style={{ fontSize: 11.5, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.55)', marginBottom: 5 }}>
              {s.period}
            </p>
            <p style={{ fontSize: 13.5, color: 'rgba(255,255,255,0.88)', lineHeight: 1.5, fontWeight: 500 }}>
              {s.text}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Card 3: Routine plan ─────────────────────────────────────────────────────
function StatusPill({ status, t }: { status: 'ok' | 'replace' | 'missing'; t: Translations }) {
  const cfg = {
    ok:      { label: t.status.ok,      color: '#9DEFD6', bg: 'rgba(157,239,214,0.14)', border: 'rgba(157,239,214,0.32)' },
    replace: { label: t.status.replace, color: '#FFB6E0', bg: 'rgba(255,182,224,0.14)', border: 'rgba(255,182,224,0.32)' },
    missing: { label: t.status.missing, color: '#C5B6FF', bg: 'rgba(197,182,255,0.14)', border: 'rgba(197,182,255,0.36)' },
  }[status];
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center',
      padding: '3px 10px', borderRadius: 100,
      fontSize: 11, fontWeight: 700,
      background: cfg.bg, color: cfg.color,
      border: `1px solid ${cfg.border}`,
      lineHeight: 1.4, flexShrink: 0,
      letterSpacing: '0.01em',
    }}>{cfg.label}</span>
  );
}

function RoutineCard({ t }: { t: Translations }) {
  const [tab, setTab] = useState<'morning' | 'evening'>('morning');

  // Status pattern is a fixed sequence per row; labels come from translations.
  const morningStatuses: ('ok' | 'replace' | 'missing')[] = ['ok', 'replace', 'replace', 'ok'];
  const eveningStatuses: ('ok' | 'replace' | 'missing')[] = ['ok', 'missing', 'replace', 'ok'];
  const data = {
    morning: t.preview.routine.steps.morning.map((label, i) => ({ n: i + 1, label, status: morningStatuses[i] })),
    evening: t.preview.routine.steps.evening.map((label, i) => ({ n: i + 1, label, status: eveningStatuses[i] })),
  };

  return (
    <div style={cardBase}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <CardHeader
          label={t.preview.routine.title}
          accent="rgba(158,216,255,0.22)"
          icon={
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
              <circle cx="8" cy="8" r="5.5" stroke="white" strokeWidth="1.4" />
              <path d="M8 3v5l3 1.5" stroke="white" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          }
        />
        {/* Tabs */}
        <div style={{
          display: 'flex',
          background: 'rgba(255,255,255,0.06)',
          border: '1px solid rgba(255,255,255,0.12)',
          borderRadius: 10,
          padding: 3,
          gap: 2,
        }}>
          {(['morning', 'evening'] as const).map(tt => (
            <button
              key={tt}
              onClick={() => setTab(tt)}
              style={{
                padding: '6px 12px', borderRadius: 7, border: 'none',
                cursor: 'pointer', fontFamily: 'Manrope,sans-serif',
                fontSize: 11, fontWeight: 700,
                background: tab === tt ? 'rgba(255,255,255,0.18)' : 'transparent',
                color: tab === tt ? 'white' : 'rgba(255,255,255,0.50)',
                transition: 'all 0.18s ease',
              }}
            >
              {tt === 'morning' ? t.preview.routine.tabs.morning : t.preview.routine.tabs.evening}
            </button>
          ))}
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {data[tab].map(s => (
          <div key={s.n} style={{
            display: 'flex', alignItems: 'center', gap: 12,
            padding: '11px 13px',
            background: 'rgba(255,255,255,0.05)',
            border: '1px solid rgba(255,255,255,0.10)',
            borderRadius: 12,
          }}>
            <div style={{
              width: 26, height: 26, borderRadius: 8,
              background: 'rgba(255,255,255,0.07)',
              border: '1px solid rgba(255,255,255,0.16)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: 'rgba(255,255,255,0.68)', fontSize: 11, fontWeight: 700, flexShrink: 0,
            }}>{s.n}</div>
            <span style={{ fontSize: 13.5, fontWeight: 600, color: 'rgba(255,255,255,0.92)', flex: 1 }}>{s.label}</span>
            <StatusPill status={s.status} t={t} />
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Section ──────────────────────────────────────────────────────────────────
export default function ReportPreviewBlock() {
  const t = useT();
  const sectionRef = useRef<HTMLElement>(null);
  const headRef    = useRef<HTMLDivElement>(null);
  useTrackSection('preview', sectionRef);
  const cardsRef   = useRef<HTMLDivElement>(null);
  const ctaRef     = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo(headRef.current,
        { opacity: 0, y: 28 },
        { opacity: 1, y: 0, duration: 0.75, ease: 'power2.out',
          scrollTrigger: { trigger: sectionRef.current, start: 'top 78%' } }
      );
      gsap.fromTo(
        cardsRef.current?.querySelectorAll('.rp-card') || [],
        { opacity: 0, y: 32, scale: 0.97 },
        { opacity: 1, y: 0, scale: 1, duration: 0.65, stagger: 0.10, ease: 'power2.out',
          scrollTrigger: { trigger: cardsRef.current, start: 'top 80%' } }
      );
      gsap.fromTo(ctaRef.current,
        { opacity: 0, y: 20 },
        { opacity: 1, y: 0, duration: 0.6, ease: 'power2.out',
          scrollTrigger: { trigger: ctaRef.current, start: 'top 88%' } }
      );
    });
    return () => ctx.revert();
  }, []);

  return (
    <section
      ref={sectionRef}
      id="preview"
      style={{
        position: 'relative',
        padding: 'clamp(56px, 7vw, 96px) clamp(24px, 5vw, 80px)',
        zIndex: 1,
        background: 'transparent',
      }}
    >
      <div style={{ maxWidth: 1100, margin: '0 auto' }}>

        {/* Heading */}
        <div ref={headRef} style={{ textAlign: 'center', marginBottom: 'clamp(36px, 4.5vw, 56px)', opacity: 0 }}>
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: 7,
            background: 'rgba(255,255,255,0.10)',
            border: '1px solid rgba(255,255,255,0.20)',
            borderRadius: 100,
            padding: '6px 14px',
            marginBottom: 18,
          }}>
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
              <rect x="1.5" y="2" width="9" height="8" rx="1.5" stroke="rgba(255,255,255,0.7)" strokeWidth="1.2" />
              <path d="M3.5 5h5M3.5 7h3.5" stroke="rgba(255,255,255,0.7)" strokeWidth="1.2" strokeLinecap="round" />
            </svg>
            <span style={{ fontSize: 11, fontWeight: 700, color: 'rgba(255,255,255,0.75)', letterSpacing: '0.10em', textTransform: 'uppercase' }}>
              {t.preview.eyebrow}
            </span>
          </div>

          <h2 style={{
            fontSize: 'clamp(26px, 3.2vw, 46px)',
            fontWeight: 800,
            lineHeight: 1.15,
            letterSpacing: '-0.022em',
            color: 'white',
            marginBottom: 14,
          }}>
            {t.preview.titleA}<br />
            <span style={{ opacity: 0.72 }}>{t.preview.titleB}</span>
          </h2>
          <p style={{ fontSize: 'clamp(14px, 1.6vw, 17px)', color: 'rgba(255,255,255,0.62)', lineHeight: 1.65, maxWidth: 540, margin: '0 auto' }}>
            {t.preview.subtitle}
          </p>
        </div>

        {/* Cards: top row sharp (the "wow" cards), bottom row blurred as a
            preview-tease — the embedded CTA pushes users to /example for the
            full report so they can really judge the depth of analysis. */}
        <div ref={cardsRef} style={{ display: 'flex', flexDirection: 'column', gap: 'clamp(14px, 2vw, 22px)' }}>

          {/* Row 1 — с blur-fade снизу как намёк на полный отчёт */}
          <div style={{ position: 'relative', borderRadius: 22, overflow: 'hidden' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 'clamp(14px, 2vw, 22px)' }} className="rp-grid">
              <div className="rp-card" style={{ opacity: 0 }}><ProductSwapsCard t={t} /></div>
              <div className="rp-card" style={{ opacity: 0 }}><PersonalIngredientsCard t={t} /></div>
            </div>

            {/* Blur fade снизу */}
            <div aria-hidden="true" className="rp-progressive-blur" />
            <div aria-hidden="true" className="rp-tint-fade" />

            {/* CTA оверлей */}
            <div className="rp-overlay-wrap">
              <Link
                to="/example"
                target="_blank"
                rel="noopener noreferrer"
                className="rp-overlay-cta"
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M2 8h11M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                {t.preview.ctaOverlay}
              </Link>
            </div>
          </div>

        </div>

        {/* Primary CTA below — "Хочу такой же план" stays as the main action */}
        <div
          ref={ctaRef}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginTop: 'clamp(32px, 4vw, 48px)',
            opacity: 0,
          }}
        >
          <Link
            to="/get-started"
            className="rp-cta rp-cta-primary"
          >
            {t.preview.ctaPrimary}
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path d="M2.5 7h9M7.5 3l4 4-4 4" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </Link>
        </div>
      </div>

      <style>{`
        @media (max-width: 720px) {
          .rp-grid { grid-template-columns: 1fr !important; }
        }

        /* ── Progressive blur: backdrop-filter masked by a top→bottom gradient.
              Top 12% is fully sharp, blur grows smoothly through the rest. ─── */
        .rp-progressive-blur {
          position: absolute;
          inset: 0;
          pointer-events: none;
          backdrop-filter: blur(10px);
          -webkit-backdrop-filter: blur(10px);
          mask-image: linear-gradient(180deg,
            transparent 0%,
            transparent 12%,
            rgba(0,0,0,0.18) 28%,
            rgba(0,0,0,0.45) 48%,
            rgba(0,0,0,0.75) 70%,
            rgba(0,0,0,0.92) 88%,
            black 100%);
          -webkit-mask-image: linear-gradient(180deg,
            transparent 0%,
            transparent 12%,
            rgba(0,0,0,0.18) 28%,
            rgba(0,0,0,0.45) 48%,
            rgba(0,0,0,0.75) 70%,
            rgba(0,0,0,0.92) 88%,
            black 100%);
        }
        .rp-tint-fade {
          position: absolute;
          inset: 0;
          pointer-events: none;
          background: linear-gradient(180deg,
            transparent 0%,
            transparent 30%,
            rgba(30,20,55,0.18) 60%,
            rgba(30,20,55,0.42) 100%);
        }
        .rp-overlay-wrap {
          position: absolute;
          inset: 0;
          display: flex;
          align-items: flex-end;
          justify-content: center;
          padding: 0 clamp(12px, 2vw, 20px) clamp(28px, 4vw, 56px);
        }

        /* ── Overlay CTA on top of the blurred preview row ────────────────── */
        .rp-overlay-cta {
          display: inline-flex;
          align-items: center;
          gap: 10px;
          padding: 16px 26px;
          border-radius: 100px;
          background: rgba(255,255,255,0.16);
          backdrop-filter: blur(20px);
          -webkit-backdrop-filter: blur(20px);
          border: 1px solid rgba(255,255,255,0.50);
          color: white;
          font-family: 'Manrope', sans-serif;
          font-weight: 700;
          font-size: 15px;
          letter-spacing: -0.01em;
          text-decoration: none;
          box-shadow:
            0 12px 36px rgba(20,10,40,0.32),
            0 0 32px rgba(255,255,255,0.10);
          transition: transform 0.22s ease, background 0.22s ease, border-color 0.22s ease, box-shadow 0.22s ease;
          text-align: center;
        }
        .rp-overlay-cta:hover {
          background: rgba(255,255,255,0.26);
          border-color: rgba(255,255,255,0.78);
          transform: translateY(-2px);
          box-shadow:
            0 16px 44px rgba(20,10,40,0.40),
            0 0 48px rgba(255,255,255,0.18);
        }

        /* ── CTA buttons: shared base ────────────────────────────────────── */
        .rp-cta {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 14px 28px;
          border-radius: 100px;
          font-family: 'Manrope', sans-serif;
          font-weight: 700;
          font-size: 15px;
          letter-spacing: -0.01em;
          text-decoration: none;
          cursor: pointer;
          transition: transform 0.22s ease;
          will-change: transform, box-shadow;
        }

        /* ── Primary (gradient): always-on warm glow + pulsing ring ───────── */
        .rp-cta-primary {
          color: white;
          border: none;
          background: linear-gradient(135deg, #F36BAF 0%, #8B7CFF 100%);
          animation: rpCtaPulse 2.6s ease-in-out infinite;
        }
        @keyframes rpCtaPulse {
          0%, 100% {
            /* drop-shadow + steady halo + pulse ring (start) */
            box-shadow:
              0 8px 28px rgba(243,107,175,0.40),
              0 0 36px rgba(243,107,175,0.32),
              0 0 64px rgba(139,124,255,0.18),
              0 0 0 0 rgba(243,107,175,0.45);
            transform: scale(1);
          }
          50% {
            box-shadow:
              0 12px 42px rgba(243,107,175,0.58),
              0 0 52px rgba(243,107,175,0.48),
              0 0 80px rgba(139,124,255,0.26),
              0 0 0 12px rgba(243,107,175,0);
            transform: scale(1.025);
          }
        }
        .rp-cta-primary:hover {
          animation-play-state: paused;
          transform: translateY(-2px) scale(1.025);
          box-shadow:
            0 14px 46px rgba(243,107,175,0.62),
            0 0 56px rgba(243,107,175,0.52),
            0 0 88px rgba(139,124,255,0.30),
            0 0 0 4px rgba(243,107,175,0.20);
        }

        /* ── Secondary (glass): always-on cool halo + offset breathing ────── */
        .rp-cta-secondary {
          gap: 7px;
          padding: 14px 24px;
          color: rgba(255,255,255,0.92);
          font-weight: 600;
          background: rgba(255,255,255,0.10);
          backdrop-filter: blur(14px);
          -webkit-backdrop-filter: blur(14px);
          border: 1px solid rgba(255,255,255,0.24);
          animation: rpCtaGlow 3.2s ease-in-out infinite;
          animation-delay: 1.3s;
        }
        @keyframes rpCtaGlow {
          0%, 100% {
            box-shadow:
              0 0 24px rgba(255,255,255,0.14),
              0 0 48px rgba(200,210,255,0.10),
              0 0 0 0 rgba(255,255,255,0);
            border-color: rgba(255,255,255,0.28);
            transform: scale(1);
          }
          50% {
            box-shadow:
              0 0 36px rgba(255,255,255,0.24),
              0 0 64px rgba(200,210,255,0.18),
              0 0 0 1px rgba(255,255,255,0.16);
            border-color: rgba(255,255,255,0.48);
            transform: scale(1.018);
          }
        }
        .rp-cta-secondary:hover {
          animation-play-state: paused;
          background: rgba(255,255,255,0.18);
          border-color: rgba(255,255,255,0.58);
          box-shadow:
            0 0 40px rgba(255,255,255,0.28),
            0 0 72px rgba(200,210,255,0.20),
            0 0 0 1px rgba(255,255,255,0.22);
          transform: translateY(-2px) scale(1.018);
        }

        /* Respect users who turn motion off. */
        @media (prefers-reduced-motion: reduce) {
          .rp-cta-primary, .rp-cta-secondary { animation: none; }
        }
      `}</style>
    </section>
  );
}
